import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, Plus, Trash2, Edit2, GripVertical, ArrowLeft, Save, Info, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../../config/api';
import axios from 'axios';

const REPRESENTS_LABEL = {
  alphabet: 'Phoneme(s) represented',
  abjad: 'Consonant phoneme(s)',
  abugida: 'Vowel+consonant sequence(s)',
  syllabic: 'Syllable represented',
  morphographic: 'Morpheme represented',
};

const WritingSystemDetailPage = () => {
  const { conlang , canEdit} = useOutletContext();
  const { conlangId, systemId } = useParams();
  const navigate = useNavigate();
  const [system, setSystem] = useState(null);
  const [graphemes, setGraphemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGrapheme, setEditingGrapheme] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [previewInput, setPreviewInput] = useState('');

  const [character, setCharacter] = useState('');
  const [gName, setGName] = useState('');
  const [represents, setRepresents] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => { fetchData(); }, [conlangId, systemId]);

  const fetchData = async () => {
    try {
      const [sysRes, graphRes] = await Promise.all([
        axios.get(`${API}/conlangs/${conlangId}/writing-systems/${systemId}`, { withCredentials: true }),
        axios.get(`${API}/conlangs/${conlangId}/writing-systems/${systemId}/graphemes`, { withCredentials: true }),
      ]);
      setSystem(sysRes.data);
      setGraphemes(graphRes.data);
    } catch {
      toast.error('Failed to load writing system');
      navigate(`/conlang/${conlangId}/writing`);
    }
    finally { setLoading(false); }
  };

  const resetForm = () => { setCharacter(''); setGName(''); setRepresents(''); setNotes(''); setEditingGrapheme(null); };

  const openEdit = (g) => {
    setEditingGrapheme(g);
    setCharacter(g.character); setGName(g.name); setRepresents(g.represents || ''); setNotes(g.notes || '');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const data = { character, name: gName, represents: represents || null, notes: notes || null };
    try {
      if (editingGrapheme) {
        const res = await axios.put(
          `${API}/conlangs/${conlangId}/writing-systems/${systemId}/graphemes/${editingGrapheme.grapheme_id}`,
          data, { withCredentials: true }
        );
        setGraphemes(graphemes.map(g => g.grapheme_id === editingGrapheme.grapheme_id ? res.data : g));
        toast.success('Grapheme updated');
      } else {
        const res = await axios.post(
          `${API}/conlangs/${conlangId}/writing-systems/${systemId}/graphemes`,
          data, { withCredentials: true }
        );
        setGraphemes([...graphemes, res.data]);
        toast.success('Grapheme added');
      }
      setDialogOpen(false); resetForm();
    } catch { toast.error('Failed to save grapheme'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this grapheme?')) return;
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/writing-systems/${systemId}/graphemes/${id}`, { withCredentials: true });
      setGraphemes(graphemes.filter(g => g.grapheme_id !== id));
      toast.success('Grapheme deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const moveGrapheme = (index, direction) => {
    const newList = [...graphemes];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newList.length) return;
    [newList[index], newList[targetIndex]] = [newList[targetIndex], newList[index]];
    setGraphemes(newList);
    setOrderChanged(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await axios.put(
        `${API}/conlangs/${conlangId}/writing-systems/${systemId}/canonical-order`,
        { grapheme_ids: graphemes.map(g => g.grapheme_id) },
        { withCredentials: true }
      );
      setOrderChanged(false);
      toast.success('Canonical order saved');
    } catch { toast.error('Failed to save order'); }
    finally { setSavingOrder(false); }
  };

  const representsLabel = system?.type === 'alphabetic'
    ? REPRESENTS_LABEL[system?.alphabetic_subtype] || 'Represents'
    : REPRESENTS_LABEL[system?.type] || 'Represents';

  const isAlphabetic = system?.type === 'alphabetic';

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  if (!system) return null;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/conlang/${conlangId}/writing`)} data-testid="back-to-writing">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="ws-detail-title">{system.name}</h2>
            <p className="text-foreground-muted mt-0.5 text-sm">{system.description}</p>
          </div>
        </div>
        {canEdit && (
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary hover:bg-primary/90" data-testid="add-grapheme-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Grapheme
        </Button>
        )}
      </div>

      {/* Graphemes Table */}
      {graphemes.length > 0 ? (
        <Card className="bg-background-paper border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg text-foreground">
                  Graphemes {isAlphabetic && <span className="text-foreground-muted font-normal text-sm ml-2">(in canonical order)</span>}
                </CardTitle>
                <CardDescription>{graphemes.length} grapheme{graphemes.length !== 1 ? 's' : ''} defined</CardDescription>
              </div>
              {isAlphabetic && orderChanged && canEdit && (
                <Button onClick={saveOrder} disabled={savingOrder} size="sm" className="bg-secondary hover:bg-secondary/90" data-testid="save-order-btn">
                  {savingOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Order
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1" data-testid="graphemes-list">
              {graphemes.map((g, i) => (
                <div key={g.grapheme_id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent/50 transition-colors group">
                  {isAlphabetic && (
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => moveGrapheme(i, -1)}
                        disabled={i === 0}
                        className="text-foreground-subtle hover:text-foreground disabled:opacity-20 text-xs"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => moveGrapheme(i, 1)}
                        disabled={i === graphemes.length - 1}
                        className="text-foreground-subtle hover:text-foreground disabled:opacity-20 text-xs"
                      >
                        ▼
                      </button>
                    </div>
                  )}
                  <div className="w-14 h-14 rounded-lg bg-accent flex items-center justify-center">
                    <span className="font-conlang text-2xl text-foreground">{g.character}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-foreground font-medium">{g.name}</span>
                      {isAlphabetic && <span className="text-foreground-subtle text-xs">#{i + 1}</span>}
                    </div>
                    {g.represents && (
                      <p className="text-foreground-muted text-sm truncate">{g.represents}</p>
                    )}
                    {g.notes && <p className="text-foreground-subtle text-xs truncate">{g.notes}</p>}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(g)}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(g.grapheme_id)} className="text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-background-paper border-border border-dashed">
          <CardContent className="p-12 text-center">
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">No graphemes yet</h3>
            <p className="text-foreground-muted mb-4">Start adding the characters of this writing system.</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" /> Add Grapheme
            </Button>
          </CardContent>
        </Card>
      )}

      {isAlphabetic && graphemes.length > 0 && (
        <div className="p-4 rounded-md bg-secondary/10 border border-secondary/30 flex gap-3">
          <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
          <div className="text-secondary text-sm">
            <strong>Canonical order</strong> determines how words are sorted in this writing system. Use the ▲▼ arrows to reorder graphemes, then click "Save Order" to persist.
          </div>
        </div>
      )}

      {/* Preview Script Section */}
      {graphemes.length > 0 && (
        <Card className="bg-background-paper border-border" data-testid="preview-script-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-foreground flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Preview Script
            </CardTitle>
            <CardDescription>Type romanized text to see it rendered using your graphemes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              value={previewInput}
              onChange={(e) => setPreviewInput(e.target.value)}
              placeholder="e.g., /a/ /b/ /k/ or type phoneme representations..."
              className="bg-background border-border"
              data-testid="preview-input"
            />
            <div className="p-6 bg-accent/30 rounded-lg min-h-[80px] flex items-center justify-center" data-testid="preview-output">
              {previewInput ? (
                <div className={`text-4xl tracking-widest ${system?.line_orientation === 'rtl' ? 'direction-rtl' : ''}`} style={{ direction: system?.line_orientation === 'rtl' ? 'rtl' : 'ltr' }}>
                  {(() => {
                    // Build mapping: represents → character (sorted by longest match first)
                    const mapping = graphemes
                      .filter(g => g.represents)
                      .map(g => ({ key: g.represents, char: g.character }))
                      .sort((a, b) => b.key.length - a.key.length);
                    
                    let result = [];
                    let input = previewInput;
                    let pos = 0;
                    
                    while (pos < input.length) {
                      let matched = false;
                      for (const m of mapping) {
                        if (input.substring(pos).startsWith(m.key)) {
                          result.push(<span key={pos} className="text-foreground">{m.char}</span>);
                          pos += m.key.length;
                          matched = true;
                          break;
                        }
                      }
                      if (!matched) {
                        // Keep unmapped chars as-is but dim
                        result.push(<span key={pos} className="text-foreground-subtle opacity-40">{input[pos]}</span>);
                        pos++;
                      }
                    }
                    return result;
                  })()}
                </div>
              ) : (
                <p className="text-foreground-subtle text-sm italic">Type above to preview your script</p>
              )}
            </div>
            {previewInput && (
              <p className="text-xs text-foreground-subtle">
                Mapping: {graphemes.filter(g => g.represents).map(g => `${g.represents} → ${g.character}`).join(', ')}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Grapheme Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg" data-testid="grapheme-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">{editingGrapheme ? 'Edit' : 'Add'} Grapheme</DialogTitle>
            <DialogDescription className="text-foreground-muted">Define a character in this writing system</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Character</Label>
                <Input value={character} onChange={(e) => setCharacter(e.target.value)} placeholder="e.g., α, あ, 木" className="bg-background border-border font-conlang text-2xl text-center" maxLength={4} required data-testid="grapheme-char" />
              </div>
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={gName} onChange={(e) => setGName(e.target.value)} placeholder="e.g., alpha, ka, tree" className="bg-background border-border" required data-testid="grapheme-name" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{representsLabel}</Label>
              <Input value={represents} onChange={(e) => setRepresents(e.target.value)} placeholder={
                system?.type === 'syllabic' ? 'e.g., ka, ki, ku' :
                system?.type === 'morphographic' ? 'e.g., tree, water, person' :
                system?.alphabetic_subtype === 'abjad' ? 'e.g., /k/, /t/' :
                system?.alphabetic_subtype === 'abugida' ? 'e.g., ka, ki, ku' :
                'e.g., /a/, /k/, /θ/'
              } className="bg-background border-border font-conlang" data-testid="grapheme-represents" />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes..." className="bg-background border-border" data-testid="grapheme-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !character || !gName} data-testid="submit-grapheme">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingGrapheme ? 'Save' : 'Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WritingSystemDetailPage;
